import { useEffect, useCallback } from 'react';
import { usePolling } from '../context/PollingContext';
import { scoringRulesService } from '../services/scoringRulesService';

/**
 * Hook to integrate scoring rules with competition management
 * Automatically applies scoring rules from awards settings to active competitions
 */
export function useScoringRulesIntegration() {
  const { connected } = usePolling();

  /**
   * Apply scoring rules to a competition
   * @param {string} competitionCode - Competition code
   * @returns {Promise<boolean>} Success status
   */
  const applyScoringRulesToCompetition = useCallback(async (competitionCode) => {
    if (!connected || !competitionCode) {
      console.warn('Cannot apply scoring rules: not connected or missing competition code');
      return false;
    }

    try {
      console.log(`Applying scoring rules to competition: ${competitionCode}`);
      const success = await scoringRulesService.applyScoringRulesToCompetition(competitionCode);
      
      if (success) {
        console.log(`Successfully applied scoring rules to competition ${competitionCode}`);
      } else {
        console.warn(`Failed to apply scoring rules to competition ${competitionCode}`);
      }
      
      return success;
    } catch (error) {
      console.error('Error applying scoring rules to competition:', error);
      return false;
    }
  }, [connected]);

  /**
   * Get scoring rules for display
   * @returns {Promise<Object|null>} Scoring rules or null if none configured
   */
  const getScoringRulesForDisplay = useCallback(async () => {
    try {
      return await scoringRulesService.getScoringRulesForDisplay();
    } catch (error) {
      console.error('Error getting scoring rules for display:', error);
      return null;
    }
  }, []);

  /**
   * Clear scoring rules cache (call when rules are updated in settings)
   */
  const clearScoringRulesCache = useCallback(() => {
    scoringRulesService.clearCache();
    console.log('Scoring rules cache cleared');
  }, []);

  /**
   * Polling-based system doesn't need event listeners
   * Results are handled directly in the API calls
   */

  return {
    applyScoringRulesToCompetition,
    getScoringRulesForDisplay,
    clearScoringRulesCache
  };
}

/**
 * Hook specifically for competition creators/tabulators
 * Automatically applies scoring rules when joining competitions
 */
export function useAutoApplyScoringRules(competitionCode, role) {
  const { applyScoringRulesToCompetition } = useScoringRulesIntegration();

  useEffect(() => {
    // Only auto-apply for tabulators/admins, not judges
    if (competitionCode && (role === 'tabulator' || role === 'admin')) {
      console.log(`Auto-applying scoring rules for ${role} in competition ${competitionCode}`);
      
      // Apply scoring rules after a short delay to ensure competition is fully initialized
      const timer = setTimeout(() => {
        applyScoringRulesToCompetition(competitionCode);
      }, 1000);

      return () => clearTimeout(timer);
    }
  }, [competitionCode, role, applyScoringRulesToCompetition]);
}

export default useScoringRulesIntegration;
